import axios from "axios";
import { ApiEndpoints, parseFiltersToQuery } from "./common";
import { KaseyaInventoryItemType, ExclusionType } from "./enums";

const toastNotification = (message, severity, isStacked = true) => {
  const toastNotificationConfigs = {
    message: message,
    severity: severity,
    x: "right",
    y: "bottom",
    autoCloseDelayMs: 10000,
  };

  window.nirvana.dependencies.services.buildToastNotification(
    toastNotificationConfigs,
    undefined,
    isStacked
  );
};

const getData = async (endpoint, queryParam) => {
  let parsedUrl =
    queryParam === null || queryParam === undefined
      ? `${process.env.REACT_APP_KASEYA_STUB_API_URL}/${endpoint}`
      : `${process.env.REACT_APP_KASEYA_STUB_API_URL}/${endpoint}?${queryParam}`;

  const response = await axios.get(parsedUrl, {
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Methods": "GET",
    },
    withCredentials: true,
  });
  return response.data;
};

const getDataAsCount = async (endpoint) => {
  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL + `/${endpoint}`,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const postData = async (endpoint, body, shouldTriggerToast) => {
  const response = await axios.post(
    process.env.REACT_APP_KASEYA_STUB_API_URL + `/${endpoint}`,
    body,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  if (shouldTriggerToast) {
    toastNotification(`Changes have been saved`, "success");
  }
  return response.data;
};

const deleteData = async (endpoint, body, isTenantReset = false) => {
  const response = await axios.delete(
    process.env.REACT_APP_KASEYA_STUB_API_URL + `/${endpoint}`,
    {
      withCredentials: true,
      data: body,
    }
  );

  if (isTenantReset) {
    toastNotification(`Tenant successfully reset`, "success");
  } else {
    toastNotification(`Changes have been saved`, "success");
  }

  return response.data;
};

const fetchLastTaskResult = async (endpoint) => {
  const response = await axios.get(
    `${process.env.REACT_APP_KASEYA_STUB_API_URL}/${ApiEndpoints.TASK_RESULTS}/${endpoint}`,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const fetchAssets = async (
  filters,
  pageNumber,
  pageSize,
  targetNodeType,
  targetNodeId,
  targetNodeName,
  targetParentNodeId
) => {
  const parsedFilters = parseFiltersToQuery(filters);
  let filtersAsQuery = parsedFilters || "";
  let list = [];
  if (
    targetNodeType &&
    targetNodeId &&
    targetNodeType === KaseyaInventoryItemType.ORGANIZATION
  ) {
    list.push(`OrganizationId eq ${targetNodeId}`);
  }
  if (
    targetNodeType &&
    targetNodeId &&
    targetNodeType === KaseyaInventoryItemType.SITE
  ) {
    list.push(`SiteId eq ${targetNodeId}`);
  }
  //there is no groupId in assets response, so we have to temporarily use GroupName
  if (
    targetNodeType &&
    targetNodeId &&
    targetNodeType === KaseyaInventoryItemType.GROUP
  ) {
    list.push(
      `endswith(GroupName, '> ${targetNodeName}') and SiteId eq ${targetParentNodeId}`
    );
  }
  if (filtersAsQuery.trim() !== "") {
    list.push(filtersAsQuery);
  }
  let filterParams = list.join(" and ");

  const parsedUrl =
    pageNumber == null && pageSize == null
      ? `/${ApiEndpoints.ASSETS}?Filter=${filterParams}`
      : `/${
          ApiEndpoints.ASSETS
        }?Filter=${filterParams}&PageSize=${pageSize}&PageNumber=${
          pageNumber === 0 ? 1 : pageNumber
        }`;

  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL + parsedUrl,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const fetchInventoryEvents = async (filters, pageNumber, pageSize) => {
  const parsedFilters = parseFiltersToQuery(filters);

  let url = `/${ApiEndpoints.INVENTORY_EVENTS}?`;
  if (parsedFilters) {
    url += `Filter=${parsedFilters}&`;
  }
  const parsedUrl =
    pageNumber == null && pageSize == null
      ? url
      : `${url}PageSize=${pageSize}&PageNumber=${pageNumber}`;

  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL + parsedUrl,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const fetchInventoryEvent = async (id) => {
  let url = `/${ApiEndpoints.INVENTORY_EVENTS}/${id}`;

  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL + url,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const fetchAgentEvents = async (filters, pageNumber, pageSize) => {
  const parsedFilters = parseFiltersToQuery(filters);

  let url = `/${ApiEndpoints.AGENT_EVENTS}?`;
  if (parsedFilters) {
    url += `Filter=${parsedFilters}&`;
  }

  const parsedUrl =
    pageNumber == null && pageSize == null
      ? url
      : `${url}PageSize=${pageSize}&PageNumber=${pageNumber}`;

  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL + parsedUrl,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const handleInventoryEvent = async (eventHandlingOptions, eventId) => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_KASEYA_STUB_API_URL +
        `/${ApiEndpoints.INVENTORY_EVENTS}/${eventId}`,
      eventHandlingOptions,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 409) {
        toastNotification(
          "Another task is already in progress.",
          "warning",
          false
        );
      }
    }
  }
};

const fetchAgentsCount = async (filters) => {
  const parsedFilters = parseFiltersToQuery(filters);
  const filtersAsQuery = parsedFilters || "";

  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL +
      `/${ApiEndpoints.INVENTORY_AGENTS_COUNT}?Filter=${filtersAsQuery}`,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const triggerOnDemandInventorySync = async (inventoryOnDemandSyncDto) => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_KASEYA_STUB_API_URL +
        `/${ApiEndpoints.INVENTORY_ONDEMAND_SYNC}`,
      inventoryOnDemandSyncDto,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 409) {
        toastNotification(
          "Another task is already in progress.",
          "warning",
          false
        );
      }
    }
  }
};

const updateGzApiDetails = async (gzApiDetailsDto) => {
  const response = await axios.post(
    process.env.REACT_APP_KASEYA_STUB_API_URL +
      `/${ApiEndpoints.TENANTS}/updateapidetails`,
    gzApiDetailsDto,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const createVsaTenant = async (tenantConfiguration) => {
  const response = await axios.post(
    process.env.REACT_APP_KASEYA_STUB_API_URL + `/${ApiEndpoints.TENANTS}`,
    tenantConfiguration,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const checkApiDetails = async (gzApiDetailsDto, isLoginValidation = false) => {
  const response = await axios.post(
    process.env.REACT_APP_KASEYA_STUB_API_URL +
      `/${ApiEndpoints.TENANTS}/validateapidetails`,
    gzApiDetailsDto,
    {
      headers: {
        "X-IsLoginValidation": `${isLoginValidation}`,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const fetchTenant = async () => {
  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL + `/${ApiEndpoints.TENANTS}`,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const updateTenant = async (data) => {
  const response = await axios.put(
    process.env.REACT_APP_KASEYA_STUB_API_URL + `/${ApiEndpoints.TENANTS}`,
    data,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const triggerOnDemandAgentSync = async (agentsToSync) => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_KASEYA_STUB_API_URL +
        `/${ApiEndpoints.AGENT_ONDEMAND_SYNC}`,
      agentsToSync,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 409) {
        toastNotification(
          "Another task is already in progress.",
          "warning",
          false
        );
      }
    }
  }
};

const uninstallAgents = async (agentIds) => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_KASEYA_STUB_API_URL +
        `/${ApiEndpoints.AGENT_UNINSTALL}`,
      agentIds,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 409) {
        toastNotification(
          "Another task is already in progress.",
          "warning",
          false
        );
      }
    }
  }
};

const createScanTaskAgents = async (agentIds, scanType) => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_KASEYA_STUB_API_URL +
        `/${ApiEndpoints.AGENT_TASK_SCAN}`,
        {
          endpointIds: agentIds,
          typeOfScan: scanType
        },
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      }
    );

    if (response.data === true) {
      toastNotification("Agent scan task started successfully", "success");
    } else {
      toastNotification("Failed to create the scan task for one or more selected agents. Check the Agent Events section for details on existing issues.", "error");
    }
  } catch (error) {
    if (error.response) {
      toastNotification(
        "Failed to create the scan task for one or more selected agents. Check the Agent Events section for details on existing issues.",
        "error",
        false
      );
    }
  }
};

const excludeItem = async (itemToExclude, itemName) => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_KASEYA_STUB_API_URL +
        `/${ApiEndpoints.EXCLUDE_ITEM}`,
      itemToExclude,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      }
    );

    let message =
      itemToExclude.ExclusionType === ExclusionType.InventorySync
        ? "inventory synchronization"
        : "agent synchronization";
    toastNotification(`${itemName} is excluded from ${message}.`, "success");
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 409) {
        toastNotification(
          "Another task is already in progress.",
          "warning",
          false
        );
      }
    }
  }
};

const includeItem = async (itemToInclude, itemName) => {
  try {
    const response = await axios.post(
      process.env.REACT_APP_KASEYA_STUB_API_URL +
        `/${ApiEndpoints.INCLUDE_ITEM}`,
      itemToInclude,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
        withCredentials: true,
      }
    );

    let message =
      itemToInclude.ExclusionType === ExclusionType.InventorySync
        ? "inventory synchronization"
        : "agent synchronization";
    toastNotification(`${itemName} is included in ${message}.`, "success");
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 409) {
        toastNotification(
          "Another task is already in progress.",
          "warning",
          false
        );
      }
    }
  }
};

const fetchCompaniesForAssociation = async (companyId) => {
  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL + `/${companyId}/association`,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const fetchAssociations = async () => {
  const response = await axios.get(
    process.env.REACT_APP_KASEYA_STUB_API_URL + `/associations`,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const fetchAssociationsAsPost = async (listOfOrgs) => {
  const response = await axios.post(
    process.env.REACT_APP_KASEYA_STUB_API_URL +
      `${ApiEndpoints.ASSOCIATION_DATA}/associations`,
    listOfOrgs,
    {
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      withCredentials: true,
    }
  );
  return response.data;
};

const signOutAsync = async () => {
    let url = process.env.REACT_APP_KASEYA_STUB_API_URL +
          `/${ApiEndpoints.USER_SIGN_OUT}`

    const response = await axios.post(
        url,
        null,
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
          withCredentials: true,
        }
      );
      return response.data;
}

export {
  getData,
  getDataAsCount,
  postData,
  deleteData,
  fetchAgentsCount,
  toastNotification,
  triggerOnDemandInventorySync,
  fetchTenant,
  updateTenant,
  updateGzApiDetails,
  checkApiDetails,
  createVsaTenant,
  fetchInventoryEvents,
  fetchInventoryEvent,
  handleInventoryEvent,
  triggerOnDemandAgentSync,
  uninstallAgents,
  createScanTaskAgents,
  excludeItem,
  includeItem,
  fetchAgentEvents,
  fetchLastTaskResult,
  fetchAssets,
  fetchCompaniesForAssociation,
  fetchAssociations,
  fetchAssociationsAsPost,
  signOutAsync
};
